import {Center, chakra} from '@chakra-ui/react'
import {useLocation} from '@tanstack/react-router'
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnSizingState,
  type OnChangeFn,
  type Row,
  type RowSelectionState,
  type TableOptions,
} from '@tanstack/react-table'
import {useTranslate} from '@tolgee/react'
import {CSSProperties, Fragment, ReactNode, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {EmptyTableState, EmptyTableStateProps} from '../../../components/ui/empty-table-state.js'
import {Logo} from '../../../components/ui/logo.js'
import {BaseRowData} from '../../../types/index.js'
import {useZeroVirtualizer, type GetPageQuery, type GetSingleQuery} from '../../../utils/zero-virtualizer/index.js'
import {InfiniteTableRowV2} from './infinite-table-row-v2.js'

export type TableConfig<RowData> = Partial<Omit<TableOptions<RowData>, 'data' | 'getCoreRowModel'>>

export interface VirtualizerConfig<RowData, TStartRow = unknown> {
  getPageQuery: GetPageQuery<RowData, TStartRow>
  getSingleQuery: GetSingleQuery<RowData>
  toStartRow: (row: RowData) => TStartRow
  listContextParams: unknown
}

export interface InfiniteDataTableV2Props<RowData extends BaseRowData, TStartRow = unknown> {
  table: TableConfig<RowData>
  virtualizer: VirtualizerConfig<RowData, TStartRow>
  filtersHash?: string
  onRowClick?: (data: RowData) => void
  /**
   * Called instead of `onRowClick` when the row is clicked with shift held — including on a
   * checkbox or other control inside the row, which otherwise swallows the click.
   * `rows` are the loaded rows in display order, so a caller can select the range up to `data`.
   */
  onRowShiftClick?: (data: RowData, rows: RowData[]) => void
  onRowSelectionChange?: (selection: RowSelectionState, selectedRows: RowData[]) => void
  emptyState: EmptyTableStateProps
  hideHeader?: boolean
  hideBorder?: boolean
  rowHeight?: number
  loadingLabel?: string
  containerProps?: Record<string, unknown>
  isHeaderRow?: (current: RowData, previous: RowData | undefined) => boolean
  renderHeader?: (rowData: RowData) => ReactNode
  highlightRow?: (row: RowData) => boolean
  /** Whether a row is ticked, for tables whose selection is not the table's own row selection; defaults to `row.getIsSelected()`. */
  getRowSelected?: (row: RowData) => boolean
  /** Fixed pixel column widths with horizontal scroll contained in the table (columns must set px `size`). */
  scrollX?: boolean
  /** Number of leading columns pinned left while `scrollX` is on (includes the selection column if any). */
  frozenColumns?: number
  /** Drag handles on the header cells, for tables whose columns carry a pixel `size`. */
  resizableColumns?: boolean
  /** Where the dragged widths are kept between visits (localStorage key); omitted means in-memory. */
  columnSizingKey?: string
}

/** Persisted column widths, so a table keeps the sizing the user dragged. */
function readColumnSizing(key: string): ColumnSizingState {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as ColumnSizingState) : {}
  } catch {
    return {}
  }
}

function writeColumnSizing(key: string, sizing: ColumnSizingState) {
  try {
    localStorage.setItem(key, JSON.stringify(sizing))
  } catch {
    // a disabled or full storage must not break the table
  }
}

const ROW_HEIGHT = 48
const HEADER_EXTRA_HEIGHT = 48

export function InfiniteDataTableV2<RowData extends BaseRowData, TStartRow = unknown>({
  table: tableConfig,
  virtualizer: virtualizerConfig,
  filtersHash,
  onRowClick,
  onRowShiftClick,
  onRowSelectionChange,
  emptyState,
  hideHeader,
  // hideBorder,
  rowHeight = ROW_HEIGHT,
  loadingLabel,
  containerProps,
  isHeaderRow,
  renderHeader,
  highlightRow,
  getRowSelected,
  scrollX,
  frozenColumns = 0,
  resizableColumns,
  columnSizingKey,
}: InfiniteDataTableV2Props<RowData, TStartRow>) {
  const {t} = useTranslate('common')
  const listRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(() =>
    columnSizingKey ? readColumnSizing(columnSizingKey) : {}
  )

  const handleColumnSizingChange = useCallback<OnChangeFn<ColumnSizingState>>(
    updater => {
      setColumnSizing(previous => {
        const next = typeof updater === 'function' ? updater(previous) : updater
        if (columnSizingKey) writeColumnSizing(columnSizingKey, next)
        return next
      })
    },
    [columnSizingKey]
  )

  const {virtualizer, rowAt, complete, rowsEmpty} = useZeroVirtualizer<
    HTMLDivElement,
    Element,
    unknown,
    RowData,
    TStartRow
  >({
    estimateSize: () => rowHeight,
    overscan: 10,
    getScrollElement: () => listRef.current,
    listContextParams: virtualizerConfig.listContextParams,
    getPageQuery: virtualizerConfig.getPageQuery,
    getSingleQuery: virtualizerConfig.getSingleQuery,
    toStartRow: virtualizerConfig.toStartRow,
    getRowKey: row => row.id ?? '',
  })

  const rowAtRef = useRef(rowAt)
  rowAtRef.current = rowAt

  const rowMapRef = useRef(new Map<string, (typeof rows)[number]>())

  const virtualItems = virtualizer.getVirtualItems()

  const dataRef = useRef<RowData[]>([])

  const data = useMemo(() => {
    const newData = virtualItems.map(vi => rowAtRef.current(vi.index)).filter((r): r is RowData => r !== undefined)
    if (newData.length === dataRef.current.length && newData.every((row, i) => row === dataRef.current[i])) {
      return dataRef.current
    }
    dataRef.current = newData
    return newData
  }, [virtualItems, rowAt, virtualItems.length, virtualItems[0]?.index, virtualItems[virtualItems.length - 1]?.index])

  const getRowUrl = tableConfig.meta?.getRowUrl

  const table = useReactTable({
    data,
    getCoreRowModel: getCoreRowModel(),
    getRowId: row => row.id ?? '',
    columns: tableConfig?.columns || [],
    ...tableConfig,
    ...(resizableColumns
      ? {
          // live feedback while dragging; the widths live in the caller's own state otherwise
          columnResizeMode: 'onChange' as const,
          state: {...tableConfig.state, columnSizing},
          onColumnSizingChange: handleColumnSizingChange,
        }
      : {}),
    onRowSelectionChange: updater => {
      const newSelection = typeof updater === 'function' ? updater(table.getState().rowSelection) : updater
      tableConfig.onRowSelectionChange?.(newSelection)
      const selectedRows = Object.keys(newSelection)
        .map(key => rowMapRef.current.get(key)?.original)
        .filter(Boolean) as RowData[]
      onRowSelectionChange?.(newSelection, selectedRows)
    },
  })

  const {rows} = table.getRowModel()

  // horizontal-scroll mode: pixel widths and a frozen leading block shared by header and rows
  const headerCells = table.getHeaderGroups()[0]?.headers ?? []
  // the sizes are what the rows and the sticky offsets depend on, so they are the memo key
  const sizingKey = headerCells.map(header => `${header.id}:${header.getSize()}`).join('|')
  const {totalWidth, frozenLeftById} = useMemo(() => {
    if (!scrollX) return {totalWidth: 0, frozenLeftById: new Map<string, number>()}

    const frozen = new Map<string, number>()
    let left = 0
    headerCells.forEach((header, index) => {
      if (index < frozenColumns) {
        frozen.set(header.id, left)
        left += header.getSize()
      }
    })

    return {totalWidth: headerCells.reduce((sum, header) => sum + header.getSize(), 0), frozenLeftById: frozen}
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollX, frozenColumns, sizingKey])

  const rowMap = useMemo(() => {
    const map = new Map<string, (typeof rows)[number]>()
    for (const row of rows) {
      const id = row.original.id
      if (id) map.set(id, row)
    }
    return map
  }, [rows])

  rowMapRef.current = rowMap

  const isLoading = rowsEmpty && !complete
  const noData = rowsEmpty && complete

  const extraHeaderHeight = useMemo(() => {
    if (!isHeaderRow) return 0
    let h = 0
    for (const vi of virtualItems) {
      const current = rowAt(vi.index)
      const previous = vi.index > 0 ? rowAt(vi.index - 1) : undefined
      if (current && isHeaderRow(current, previous)) {
        h += HEADER_EXTRA_HEIGHT
      }
    }
    return h
  }, [virtualItems, rowAt, isHeaderRow])

  useEffect(() => {
    listRef.current?.scrollTo({top: 0})
  }, [location.searchStr, filtersHash])

  const handleRowClick = useCallback(
    (row: RowData, shiftKey: boolean) => {
      if (shiftKey && onRowShiftClick) {
        // shift+click extends the browser text selection, drop it instead of treating it as selected text
        window.getSelection()?.removeAllRanges()
        onRowShiftClick(
          row,
          table.getRowModel().rows.map(r => r.original)
        )
        return
      }
      if (!onRowClick) return
      const selectedText = window.getSelection()?.toString()
      if (selectedText?.length) return
      onRowClick(row)
    },
    [onRowClick, onRowShiftClick, table]
  )

  // ref keeps the delegated row handlers from going stale when onRowClick
  // changes identity without remounting this component (e.g. route param change)
  const handleRowClickRef = useRef(handleRowClick)
  handleRowClickRef.current = handleRowClick

  const onRowShiftClickRef = useRef(onRowShiftClick)
  onRowShiftClickRef.current = onRowShiftClick

  const handleRowClickEvent = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const index = e.currentTarget.getAttribute('data-index')
    if (index === null) return
    const data = rowAtRef.current(Number(index))
    if (data) handleRowClickRef.current(data, e.shiftKey)
  }, [])

  // shift+click is captured on the row, so it still reaches the range handler when the click
  // lands on a control inside the row (the selection checkbox) that stops the click from bubbling
  const handleRowShiftClickCapture = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!e.shiftKey || !onRowShiftClickRef.current) return
    const index = e.currentTarget.getAttribute('data-index')
    if (index === null) return
    const data = rowAtRef.current(Number(index))
    if (!data) return

    // the range replaces whatever the control would have done (tick just this row, open a link...)
    e.preventDefault()
    e.stopPropagation()
    handleRowClickRef.current(data, true)
  }, [])

  const handleRowKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      const index = e.currentTarget.getAttribute('data-index')
      if (index === null) return
      const data = rowAtRef.current(Number(index))
      if (data) handleRowClickRef.current(data, e.shiftKey)
    }
  }, [])

  // keep the (non-scrollable) header in step with the body's horizontal scroll
  const handleHorizontalScroll = useCallback(() => {
    if (headerRef.current && listRef.current) {
      headerRef.current.scrollLeft = listRef.current.scrollLeft
    }
  }, [])

  return (
    <chakra.div display="flex" flexDirection="column" flex="1 1 0%" minH={0} {...containerProps}>
      {!hideHeader && !noData && (
        <chakra.div
          ref={headerRef}
          display="flex"
          borderBottomWidth="1px"
          borderColor="border"
          overflowX={scrollX ? 'hidden' : undefined}
          overflowY={scrollX ? 'hidden' : undefined}
        >
          {table.getHeaderGroups().map(headerGroup => (
            <chakra.div
              key={headerGroup.id}
              display="flex"
              width="100%"
              style={scrollX ? {minWidth: `${totalWidth}px`, width: `${totalWidth}px`} : undefined}
            >
              {headerGroup.headers.map(header => {
                const frozenLeft = scrollX ? frozenLeftById.get(header.id) : undefined
                const sticky = frozenLeft !== undefined
                return (
                  <chakra.div
                    key={header.id}
                    padding="3"
                    display="flex"
                    alignItems="center"
                    fontSize="sm"
                    fontWeight="medium"
                    color="fg.subtle"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    position={sticky ? 'sticky' : resizableColumns ? 'relative' : undefined}
                    left={sticky ? `${frozenLeft}px` : undefined}
                    zIndex={sticky ? 2 : undefined}
                    bg={sticky ? 'bg' : undefined}
                    {...(scrollX
                      ? {width: `${header.getSize()}px`, minWidth: `${header.getSize()}px`, flex: '0 0 auto'}
                      : {
                          flex: '1 1 0%',
                          width: '100%',
                          maxWidth: `${header.getSize()}%`,
                          minWidth: header.column.columnDef.minSize
                            ? `${header.column.columnDef.minSize}px`
                            : undefined,
                        })}
                    data-e2e={header.id}
                    className="group"
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}

                    {resizableColumns && header.column.getCanResize() && (
                      <chakra.div
                        role="separator"
                        aria-orientation="vertical"
                        aria-label={t('resize_column')}
                        position="absolute"
                        right="0"
                        top="0"
                        bottom="0"
                        width="1"
                        cursor="col-resize"
                        userSelect="none"
                        touchAction="none"
                        // only visible while the header cell is hovered, or while dragging
                        bg={header.column.getIsResizing() ? 'cyan.solid' : undefined}
                        _groupHover={header.column.getIsResizing() ? undefined : {bg: 'border.emphasized'}}
                        onMouseDown={event => {
                          // dragging the handle must not sort the column
                          event.stopPropagation()
                          header.getResizeHandler()(event)
                        }}
                        onTouchStart={event => {
                          event.stopPropagation()
                          header.getResizeHandler()(event)
                        }}
                        onClick={event => event.stopPropagation()}
                        data-e2e={`resize-${header.id}`}
                      />
                    )}
                  </chakra.div>
                )
              })}
            </chakra.div>
          ))}
        </chakra.div>
      )}

      <chakra.div
        ref={listRef}
        overflowY={isLoading ? 'hidden' : 'auto'}
        overflowX={scrollX ? 'auto' : 'hidden'}
        flex="1 1 0%"
        onScroll={scrollX ? handleHorizontalScroll : undefined}
        data-rows-list
      >
        <chakra.div
          position="relative"
          style={{
            height: isLoading || noData ? '100%' : virtualizer.getTotalSize() + extraHeaderHeight,
            minWidth: scrollX ? `${totalWidth}px` : undefined,
          }}
          rows-virtual-list
          data-e2e="virtual-list"
        >
          {isLoading && (
            <Center width="100%" height="100%" animation="ping">
              <Logo hideText size={72} />
            </Center>
          )}

          {noData && <EmptyTableState {...emptyState} maxWidth="50%" mx="auto" textAlign="center" />}

          {rows.length > 0 &&
            (() => {
              let runningExtra = 0
              return virtualItems.map(virtualRow => {
                const rowData = rowAt(virtualRow.index)
                const adjustedStart = virtualRow.start + runningExtra

                if (!rowData) {
                  const style: CSSProperties = {
                    transform: `translateY(${adjustedStart}px)`,
                  }
                  return (
                    <chakra.div
                      key={virtualRow.key}
                      position="absolute"
                      top="0"
                      left="0"
                      height={`${rowHeight}px`}
                      width="100%"
                      display="flex"
                      alignItems="center"
                      fontSize="sm"
                      color="fg.subtle"
                      style={style}
                    >
                      <chakra.div padding="3">{loadingLabel || t('loading')}</chakra.div>
                    </chakra.div>
                  )
                }

                const prevRowData = virtualRow.index > 0 ? rowAt(virtualRow.index - 1) : undefined
                const showHeader = isHeaderRow ? isHeaderRow(rowData, prevRowData) : false

                const row = rowData?.id ? rowMap.get(rowData.id) : undefined
                const rowStart = adjustedStart + (showHeader ? HEADER_EXTRA_HEIGHT : 0)

                const elements: ReactNode[] = []

                if (showHeader && renderHeader) {
                  elements.push(
                    <chakra.div
                      key={`header-${virtualRow.key}`}
                      position="absolute"
                      top="0"
                      left="0"
                      height={`${HEADER_EXTRA_HEIGHT}px`}
                      width="100%"
                      style={{transform: `translateY(${adjustedStart}px)`}}
                    >
                      {renderHeader(rowData)}
                    </chakra.div>
                  )
                  runningExtra += HEADER_EXTRA_HEIGHT
                }

                elements.push(
                  <chakra.div
                    key={virtualRow.key}
                    position="absolute"
                    top="0"
                    left="0"
                    height={`${rowHeight}px`}
                    width="100%"
                    style={{transform: `translateY(${rowStart}px)`}}
                    willChange="transform"
                    transformOrigin="top left"
                    contain="layout paint size style"
                    backfaceVisibility="hidden"
                    data-role="table-row"
                    role="button"
                    tabIndex={0}
                    data-index={virtualRow.index}
                    data-url={getRowUrl?.(rowData)}
                    className="group"
                    onClick={handleRowClickEvent}
                    onClickCapture={handleRowShiftClickCapture}
                    onKeyDown={handleRowKeyDown}
                    _hover={{bg: 'bg.muted'}}
                    _focus={{bg: 'bg.subtle'}}
                  >
                    <InfiniteTableRowV2
                      row={row as Row<BaseRowData> | undefined}
                      highlighted={highlightRow ? highlightRow(rowData) : undefined}
                      selected={getRowSelected ? getRowSelected(rowData) : (row?.getIsSelected() ?? false)}
                      scrollX={scrollX}
                      frozenLeftById={frozenLeftById}
                      frozenColumns={frozenColumns}
                    />
                  </chakra.div>
                )

                return <Fragment key={virtualRow.key}>{elements}</Fragment>
              })
            })()}
        </chakra.div>
      </chakra.div>
    </chakra.div>
  )
}
